import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import {
  RuleFolder as ReviewIcon,
  CheckCircle as OkIcon,
  Error as BlockIcon,
  Warning as WarnIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../../utils/apiClient';

const YearChip = ({ year, variant = 'source' }) => (
  <Chip
    label={year}
    size="small"
    color={variant === 'source' ? 'warning' : 'success'}
    sx={{ mr: 0.5, mb: 0.5 }}
  />
);

const MergeReviewStep = ({ workflowData, onDataUpdate, isProcessing }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const preview = workflowData.previewData;

  useEffect(() => {
    if (!workflowData.sourceSchool || !workflowData.survivorSchool) return;
    if (preview) return; // already loaded

    const fetchPreview = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const data = await apiClient.post('/v2/merge/schools/preview', {
          source_uuid: workflowData.sourceSchool.uuid,
          survivor_uuid: workflowData.survivorSchool.uuid,
        });
        onDataUpdate({ previewData: data });
        if (data.conflicts.length > 0) {
          enqueueSnackbar('Year conflicts detected — merge cannot proceed', { variant: 'error' });
        } else {
          enqueueSnackbar('Preview loaded — no conflicts found', { variant: 'success' });
        }
      } catch (err) {
        setFetchError(err.message || 'Failed to load preview. Please try again.');
        enqueueSnackbar('Preview failed', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchPreview();
  }, [workflowData.sourceSchool, workflowData.survivorSchool]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading merge preview…</Typography>
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Alert severity="error">
        <AlertTitle>Preview Failed</AlertTitle>
        {fetchError}
      </Alert>
    );
  }

  if (!preview) return null;

  const hasConflicts = preview.conflicts.length > 0;
  const hasWarnings = preview.warnings.length > 0;

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReviewIcon color="primary" />
        Review & Validate
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review what will be transferred and confirm there are no data conflicts before executing the merge.
      </Typography>

      {/* Conflict Blocking Errors */}
      {hasConflicts && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BlockIcon />
              Merge Blocked — Year Conflicts Detected
            </Box>
          </AlertTitle>
          {preview.conflicts.map((c, i) => (
            <Typography key={i} variant="body2">
              • {c.message}
            </Typography>
          ))}
          <Typography variant="body2" sx={{ mt: 1 }}>
            Both schools have data for the same year(s). Resolve these conflicts manually before merging.
          </Typography>
        </Alert>
      )}

      {/* Non-blocking warnings */}
      {hasWarnings && (
        <Alert severity="warning" icon={<WarnIcon />} sx={{ mb: 3 }}>
          <AlertTitle>Warnings (non-blocking)</AlertTitle>
          {preview.warnings.map((w, i) => (
            <Typography key={i} variant="body2">
              • {w}
            </Typography>
          ))}
        </Alert>
      )}

      {/* No conflicts */}
      {!hasConflicts && (
        <Alert severity="success" icon={<OkIcon />} sx={{ mb: 3 }}>
          <AlertTitle>No Conflicts — Safe to Merge</AlertTitle>
          Enrollment years do not overlap. All historical data can be transferred without conflicts.
        </Alert>
      )}

      {/* Enrollment Year Timeline */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Enrollment Year Timeline
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" color="warning.main" gutterBottom>
                Source: {preview.source.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                NCES {preview.source.ncessch} · {preview.source.district}
              </Typography>
              <Box>
                {preview.year_ranges.source_enrollment.length > 0 ? (
                  preview.year_ranges.source_enrollment.map((y) => (
                    <YearChip key={y} year={y} variant="source" />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No enrollment data
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
              <ArrowIcon color="action" sx={{ fontSize: 32 }} />
              <Typography variant="caption" display="block" color="text.secondary">
                merges into
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                Survivor: {preview.survivor.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                NCES {preview.survivor.ncessch} · {preview.survivor.district}
              </Typography>
              <Box>
                {preview.year_ranges.survivor_enrollment.length > 0 ? (
                  preview.year_ranges.survivor_enrollment.map((y) => (
                    <YearChip key={y} year={y} variant="survivor" />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No enrollment data
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          {!hasConflicts && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Combined timeline after merge
              </Typography>
              <Box>
                {[
                  ...preview.year_ranges.source_enrollment.map((y) => ({ y, variant: 'source' })),
                  ...preview.year_ranges.survivor_enrollment.map((y) => ({ y, variant: 'survivor' })),
                ]
                  .sort((a, b) => a.y - b.y)
                  .map(({ y, variant }) => (
                    <YearChip key={`combined-${y}`} year={y} variant={variant} />
                  ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Migration Inventory */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Data to be Transferred
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Table</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Rows to Transfer</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Notes</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>school_enrollments</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={preview.rows_to_migrate.enrollment}
                      size="small"
                      color={preview.rows_to_migrate.enrollment > 0 ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>Grade-level enrollment by year</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>school_frl</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={preview.rows_to_migrate.frl}
                      size="small"
                      color={preview.rows_to_migrate.frl > 0 ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>Free/Reduced Lunch counts by year</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>school_grades_offered</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={preview.rows_to_migrate.grades_offered}
                      size="small"
                      color={preview.rows_to_migrate.grades_offered > 0 ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>Grade offerings by year</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>school_directory</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={preview.rows_to_migrate.directory}
                      size="small"
                      color={preview.rows_to_migrate.directory > 0 ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>Historical NCES directory entries</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>school_names</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={preview.rows_to_migrate.names}
                      size="small"
                      color={preview.rows_to_migrate.names > 0 ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>Historical display names</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Name History */}
      {preview.name_history.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Name History from Source School
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These name records will be transferred to the survivor, preserving the full rename history.
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Year</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Display Name</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.name_history.map((n) => (
                    <TableRow key={n.data_year}>
                      <TableCell>{n.data_year}</TableCell>
                      <TableCell>{n.display_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {hasConflicts ? (
        <Alert severity="error">
          Conflicts must be resolved before you can proceed. Go back and select different schools,
          or resolve the conflicting data directly in the database.
        </Alert>
      ) : (
        <Alert severity="info">
          Review the information above. If everything looks correct, click{' '}
          <strong>Continue to Merge</strong> to proceed to the final confirmation step.
        </Alert>
      )}
    </Box>
  );
};

export default MergeReviewStep;
